// src/frontend/src/AutobuyService.js
import { fetchWithAuth } from "./utils/ApiUtils";
import { config } from "./Constants";

const URL = config.url;
const BASE_URL = `${URL}/api/v1/autobuys`;
const CREDITS_URL = `${URL}/api/v1/credits`;

export const AutobuyService = {
    /**
     * Get all autobuys for the current user
     */
    getUserAutobuys: async () => {
        try {
            const response = await fetchWithAuth(BASE_URL);
            if (!response.ok) {
                throw new Error('Failed to fetch autobuys');
            }
            const data = await response.json();
            return data.data || [];
        } catch (error) {
            console.error('Error fetching autobuys:', error);
            throw error;
        }
    },

    /**
     * Create an autobuy for the current user
     */
    createAutobuy: async (stockSku) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/${stockSku}`, {
                method: 'POST'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create autobuy');
            }

            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error('Error creating autobuy:', error);
            throw error;
        }
    },

    /**
     * Cancel an autobuy for the current user
     */
    cancelAutobuy: async (stockSku) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/${stockSku}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to cancel autobuy');
            }

            return true;
        } catch (error) {
            console.error('Error cancelling autobuy:', error);
            throw error;
        }
    },

    /**
     * Check if a stock is set for autobuy by the current user
     */
    isStockSetForAutobuy: async (stockSku) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/${stockSku}/status`);

            if (!response.ok) {
                throw new Error('Failed to check autobuy status');
            }

            const data = await response.json();
            return data.data || false;
        } catch (error) {
            console.error('Error checking if stock is set for autobuy:', error);
            return false; // Default to false if there's an error
        }
    },

    /**
     * Get the number of autobuy credits for the current user
     */
    getUserCredits: async () => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/credits`);

            if (!response.ok) {
                throw new Error('Failed to get user credits');
            }

            const data = await response.json();
            return data.data || 0;
        } catch (error) {
            console.error('Error getting user credits:', error);
            return 0; // Default to 0 if there's an error
        }
    },

    /**
     * Get available credit packages
     */
    getCreditPackages: async () => {
        try {
            const response = await fetchWithAuth(`${CREDITS_URL}/packages`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch credit packages');
            }
            
            const data = await response.json();
            return data.data || [];
        } catch (error) {
            console.error('Error fetching credit packages:', error);
            throw error;
        }
    },
    
    /**
     * Create a checkout session for credit purchase
     */
    createCreditCheckoutSession: async (packageId) => {
        try {
            const response = await fetchWithAuth(`${CREDITS_URL}/checkout?packageId=${packageId}`, {
                method: 'POST'
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create checkout session');
            }
            
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error('Error creating credit checkout session:', error);
            throw error;
        }
    },
    
    /**
     * Process the result of a credit purchase after Stripe checkout
     */
    processCreditPurchase: async (sessionId) => {
        try {
            const response = await fetchWithAuth(`${CREDITS_URL}/success?session_id=${sessionId}`, {
                method: 'POST'
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to process credit purchase');
            }
            
            const data = await response.json();
            return data.data;  // Returns new credit total
        } catch (error) {
            console.error('Error processing credit purchase:', error);
            throw error;
        }
    }
};